import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { LegalContentRenderer } from "../components/LegalContentRenderer";
import { useIsMobile } from "../hooks/useIsMobile";
import { C, font } from "../styles/tokens";
import { LEGAL_CONFIG } from "../config/legalConfig";
import { fetchPublicLegalDocument } from "../api/legal";

export function TermsPage() {
  const isMobile = useIsMobile();
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    let active = true;
    fetchPublicLegalDocument("terms").then((data) => {
      if (active && data) {
        setDoc(data);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    document.title = doc?.title ? `${doc.title} - TickAndPass` : "TickAndPass - Terms & Conditions";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      "Terms and Conditions governing the use of TickAndPass services.";
  }, [doc]);

  const effectiveDate = doc?.effectiveDate || LEGAL_CONFIG.effectiveDate;
  const lastUpdated = doc?.lastUpdated || LEGAL_CONFIG.lastUpdated;
  const version = doc?.version || LEGAL_CONFIG.termsVersion;
  const title = doc?.title || "TickAndPass - Terms & Conditions";

  return (
    <PublicShell>
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          padding: isMobile ? "32px 16px 56px" : "56px 24px 80px",
          fontFamily: font,
        }}
      >
        <header
          style={{
            marginBottom: 36,
            borderBottom: `1px solid ${C.divider}`,
            paddingBottom: 24,
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 28 : 38,
              fontWeight: 800,
              color: C.navy,
              marginBottom: 12,
              letterSpacing: "-0.5px",
              lineHeight: 1.2,
            }}
          >
            {title}
          </h1>
          <div
            style={{
              fontSize: 13,
              color: C.textLight,
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <span>
              <strong>Effective Date:</strong> {effectiveDate}
            </span>
            <span aria-hidden="true">|</span>
            <span>
              <strong>Last Updated:</strong> {lastUpdated}
            </span>
            <span aria-hidden="true">|</span>
            <span>
              <strong>Version:</strong> {version}
            </span>
          </div>
        </header>

        {doc?.content ? (
          <LegalContentRenderer content={doc.content} />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 32,
              fontSize: 15,
              color: C.textDark,
              lineHeight: 1.7,
            }}
          >
          <section>
            <h2 style={sectionHeadingStyle}>1. Introduction</h2>
            <p>
              Welcome to {LEGAL_CONFIG.companyName} ("TickAndPass", "Platform",
              "we", "us", or "our"). These Terms & Conditions ("Terms") govern
              your access to and use of our digital credential verification
              platform, website, API services, and associated applications. By
              accessing, registering for, or using TickAndPass, you agree to be
              bound by these Terms and our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              2. Business Account & User Profiles
            </h2>
            <p>
              To access specific services on TickAndPass, you must register for
              an account. Users may register as individual Professionals,
              Academic Institution Officers, or Organisation Officers. You
              represent and warrant that all information provided during
              registration is accurate, current, and complete, and that you have
              full legal authority to enter into these Terms on behalf of
              yourself or the entity you represent.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>3. Services</h2>
            <p>
              TickAndPass provides source-verified credential verification
              services, digital profile management, HR candidate profile
              verification workflows, and related enterprise services in India.
              We connect professionals with official issuing institutions and
              authorised employers to perform secure, tamper-proof credential
              verifications.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>4. Fees and Payment</h2>
            <p>
              Access to verification services, annual professional memberships,
              and institution or organisation subscriptions is subject to
              applicable fees as specified on our Platform. All prices are
              listed in Indian Rupees (INR) and are processed through authorised
              payment gateway partner (Cashfree Payments). Payments must be
              completed prior to service execution.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>5. Payment Confirmation</h2>
            <p>
              Upon successful transaction authorization through our integrated
              payment gateways, a digital payment confirmation and order receipt
              will be generated. Orders are marked as confirmed only after
              backend signature verification and server-side capture.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>6. Invoices</h2>
            <p>
              TickAndPass generates automated, GST-compliant tax invoices for
              all captured transactions. Invoices are emailed to the registered
              user email address and made available for direct download within
              the account dashboard.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>7. Taxes</h2>
            <p>
              Applicable statutory taxes, including Central Goods and Services
              Tax (CGST) and State Goods and Services Tax (SGST), are calculated
              and added to transaction totals in accordance with Indian tax
              laws.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>8. Client Responsibilities</h2>
            <p>
              Users are responsible for maintaining the strict confidentiality
              of their authentication credentials. You agree not to upload
              false, fraudulent, misleading, or unauthorized credentials or
              documentation. Submitting fraudulent credentials may result in
              immediate account termination and reporting to legal authorities.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>9. Data and Privacy</h2>
            <p>
              Our collection, storage, and processing of personal information
              are governed by our{" "}
              <Link
                to="/privacy-policy"
                style={{ color: C.primary, fontWeight: 600 }}
              >
                Privacy Policy
              </Link>{" "}
              and applicable data protection regulations in India. We collect
              personal identifiers, educational records, and verification
              details solely for the purpose of delivering requested platform
              services.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>10. Confidentiality</h2>
            <p>
              Both parties agree to protect and keep confidential all non-public
              information, personal data, and proprietary documentation shared
              during the use of TickAndPass services. Confidential information
              shall not be disclosed to third parties without explicit consent,
              except as required by law.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>11. Intellectual Property</h2>
            <p>
              All materials, technology, algorithms, brand assets, logos, and UI
              components of TickAndPass are the exclusive intellectual property
              of {LEGAL_CONFIG.companyName} or its licensors. Users are granted
              a limited, non-exclusive, non-transferable licence to access and
              use the Platform in accordance with these Terms.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>12. Service Availability</h2>
            <p>
              We strive to maintain continuous platform availability. However,
              TickAndPass does not guarantee uninterrupted or error-free
              operation. Maintenance, software updates, or infrastructure
              upgrades may occasionally cause temporary service disruptions.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>13. Third-Party Services</h2>
            <p>
              TickAndPass integrates with third-party payment gateways (Cashfree
              Payments), identity verification providers, and cloud
              communication services. We are not liable for performance
              failures, delays, or outages originating from third-party
              infrastructure.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>14. Security</h2>
            <p>
              We enforce multi-layered security controls, encrypted
              communications (TLS/HTTPS), server-side signature validation, and
              strict role-based access control (RBAC). Users must promptly
              notify TickAndPass of any suspect security breaches or
              unauthorized account access.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>15. Suspension and Termination</h2>
            <p>
              TickAndPass reserves the right to suspend or terminate any user
              account or institutional subscription immediately if a violation
              of these Terms, submission of fraudulent data, or illegal activity
              is identified.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>16. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, TickAndPass
              shall not be liable for indirect, incidental, consequential,
              special, or punitive damages, or loss of profits or data arising
              out of or related to your use of the Platform. Our total aggregate
              liability shall not exceed the amount paid by you to TickAndPass
              in the 12 months preceding the event giving rise to liability.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>17. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless TickAndPass, its
              directors, officers, employees, and agents from any claims,
              liabilities, damages, losses, or expenses arising from your misuse
              of the Platform, violation of these Terms, or infringement of
              third-party rights.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>18. Force Majeure</h2>
            <p>
              Neither party shall be held liable for failure or delay in
              performing obligations under these Terms if such failure arises
              from acts of God, natural disasters, telecommunications outages,
              government actions, or event beyond reasonable control.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              19. Governing Law & Jurisdiction
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with
              the laws of India. Any legal proceedings or disputes arising under
              these Terms shall be subject to the exclusive jurisdiction of the
              competent courts in Bangalore, Karnataka.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>20. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. When
              updates are published, the "Last Updated" date will be revised
              accordingly. Continued use of the Platform following published
              updates constitutes your acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>21. Contact Information</h2>
            <p>
              If you have questions, feedback, or concerns regarding these Terms
              & Conditions, please contact us at:
            </p>
            <div style={contactCardStyle}>
              <div>
                <strong>{LEGAL_CONFIG.companyName}</strong>
              </div>
              <div>Address: {LEGAL_CONFIG.businessAddress}</div>
              <div>
                Support Email:{" "}
                <a
                  href={`mailto:${LEGAL_CONFIG.supportEmail}`}
                  style={{
                    color: C.primary,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  {LEGAL_CONFIG.supportEmail}
                </a>
              </div>
              <div>
                Privacy Email:{" "}
                <a
                  href={`mailto:${LEGAL_CONFIG.privacyEmail}`}
                  style={{
                    color: C.primary,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  {LEGAL_CONFIG.privacyEmail}
                </a>
              </div>
            </div>
          </section>
        </div>
        )}
      </div>
    </PublicShell>
  );
}

const sectionHeadingStyle = {
  fontSize: 20,
  fontWeight: 700,
  color: C.navy,
  marginBottom: 10,
  letterSpacing: "-0.2px",
};

const contactCardStyle = {
  marginTop: 14,
  padding: 16,
  borderRadius: 8,
  background: C.bgMuted,
  border: `1px solid ${C.divider}`,
  fontSize: 14,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};
